import * as Three from 'three';
import CameraControls from 'camera-controls';
import _ from 'lodash';
import FFTChartMerge from './FFTChartMerge';

/**
 * FFT 3D 차트 (InstancedMesh 버전)
 *
 * FFTChartMerge 와 동일한 인터페이스(addData / addDataAll / clearCanvas / initRender / 카메라 API)를 유지하면서
 * 막대 렌더링만 교체한다. 가이드·그리드·라벨·카메라 코드는 FFTChartMerge 를 그대로 상속한다.
 *
 * 변경점
 *  - 프레임마다 BoxGeometry 60개 생성 + 정점별 색 계산 + merge  →  InstancedMesh 1개(단위 박스, maxTimeline × hzCount 슬롯 링버퍼)
 *    프레임 추가 = 인스턴스 행렬 hzCount개 갱신. draw call 은 막대 1 + 바닥판 1.
 *  - 블록 색상 = 월드 높이 y 기준 graphColors(y / 13)  (원본과 동일 규칙) 을
 *    256 샘플 그라디언트 텍스처로 만들어 프래그먼트 셰이더에서 샘플링. 세그먼트 분할 불필요.
 *  - 머티리얼 MeshLambertMaterial(옵션으로 physical), FrontSide(닫힌 박스라 양면 불필요), antialias 옵션화.
 *  - 오래된 프레임은 슬롯 재사용이므로 geometry 누수 없음. unmount 시 renderer/geometry/texture dispose.
 *
 * 추가 옵션 (options)
 *  - barMaterial : 'lambert' (기본) | 'physical'
 *  - barDoubleSide : false (기본)
 *  - antialias : true (기본)
 *  - pixelRatio : window.devicePixelRatio (기본)
 *
 * FFTChartMerge 는 fallback 용으로 그대로 둔다.
 */
export default class FFTChartInstanced extends FFTChartMerge {
    // 막대/바닥판 인스턴스
    barGroup;
    barMesh;
    barGeometry;
    barMaterialObj;
    plateMesh;
    plateGeometry;
    plateMaterial;
    gradientTexture;

    slotCount = 0;         // maxTimeline
    barsPerFrame = 0;      // hzCount (spacing 포함)
    rafStarted = false;
    lastAddDataMs = 0;
    lastRenderMs = 0;

    // 재사용 임시 객체
    _m4 = new Three.Matrix4();
    _pos = new Three.Vector3();
    _quat = new Three.Quaternion();
    _scale = new Three.Vector3();

    constructor(props) {
        super(props);
        const options = props.options || {};
        this.barMaterialKind = options.barMaterial === 'physical' ? 'physical' : 'lambert';
        this.barDoubleSide = options.barDoubleSide === true;
        this.antialias = options.antialias === undefined ? true : options.antialias;
        this.pixelRatio = options.pixelRatio || window.devicePixelRatio;
    }

    componentDidMount() {
        super.componentDidMount();
        // 진단용 노출 (배포 후 CDP 콘솔에서 window.__fftChart.getStats() 로 렌더 비용/누수 확인)
        window.__fftChart = this;
    }

    componentWillUnmount() {
        if (window.__fftChart === this) delete window.__fftChart;
        this.dispose();
    }

    /**
     * 3D 초기화. 렌더러/카메라/조명은 FFTChartMerge.init3D 와 동일하되 antialias, pixelRatio 옵션 적용.
     */
    init3D() {
        this.clock = new Three.Clock();

        this.renderer = new Three.WebGLRenderer({
            canvas: this.canvas,
            antialias: this.antialias,
            alpha: true,
            preserveDrawingBuffer: true
        });
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.setPixelRatio(this.pixelRatio);
        this.renderer.setSize(this.container.offsetWidth, this.height);

        this.scene = new Three.Scene();

        const width = this.canvas.width;
        const height = this.canvas.height;

        this.camera = new Three.OrthographicCamera(
            -width / 2, width / 2,
            height / 2, -height / 2,
            1, 1000
        );

        this.cameraControls = new CameraControls(this.camera, this.renderer.domElement);
        this.cameraControls.mouseButtons.left = CameraControls.ACTION.TRUCK;
        this.cameraControls.mouseButtons.right = CameraControls.ACTION.ROTATE;
        this.cameraControls.mouseButtons.wheel = CameraControls.ACTION.ZOOM;
        this.cameraControls.enabled = false;
        this.camera.zoom = 1;
        this.cameraControls.setLookAt(this.cameraPosition['side']);

        this.cameraControls.addEventListener('update', (val) => {
            this.currentCamera.target = val.target._target;
            this.currentCamera.position = this.cameraControls.getPosition();
            if (this.cameraUpdate) {
                this.cameraUpdate(this.currentCamera);
            }
        });

        // 시간 가이드(라인/텍스트)용 그룹. FFTChartMerge 와 같은 이름을 유지해 상속 코드와 호환.
        this.graphGroup = new Three.Object3D();
        this.graphGroup.position.x = this.gridSize;
        this.scene.add(this.graphGroup);

        // 막대 그룹 (graphGroup 과 같은 x 이동을 따라간다)
        this.barGroup = new Three.Object3D();
        this.barGroup.position.x = this.gridSize;
        this.scene.add(this.barGroup);

        // Hemi Light
        const hemiLight = new Three.HemisphereLight(0xffffff, 0xffffff, 0.6);
        hemiLight.color.setHSL(0.6, 1, 0.6);
        hemiLight.groundColor.setHSL(0.095, 1, 0.75);
        hemiLight.position.set(0, 50, 0);
        this.scene.add(hemiLight);

        // Direction Light (그림자 맵은 renderer.shadowMap 이 꺼져 있어 원본에서도 사용되지 않았으므로 생략)
        const dirLight = new Three.DirectionalLight(0xffffff, 1);
        dirLight.color.setHSL(0.1, 1, 0.95);
        dirLight.position.set(-1, 1.75, 1);
        dirLight.position.multiplyScalar(30);
        this.scene.add(dirLight);

        // 상속 코드(drawGuide 등)가 참조하는 머티리얼
        this.graphMaterial = new Three.MeshPhysicalMaterial({
            vertexColors: Three.VertexColors,
            side: Three.DoubleSide,
            flatShading: true,
        });

        this.initBars();
    }

    /**
     * 막대/바닥판 InstancedMesh 생성
     */
    initBars() {
        this.slotCount = this.maxTimeline;
        this.barsPerFrame = this.hzCount;
        const capacity = this.slotCount * this.barsPerFrame;
        const side = this.barDoubleSide ? Three.DoubleSide : Three.FrontSide;

        this.gradientTexture = this.createGradientTexture(this.graphColors);

        // 단위 박스: 바닥이 y=0, 높이 1. 인스턴스 scale.y = boxHeight
        this.barGeometry = new Three.BoxBufferGeometry(this.gridSize, 1, this.gridSize);
        this.barGeometry.translate(0, 0.5, 0);

        this.barMaterialObj = this.createBarMaterial(this.barMaterialKind, side, this.gradientTexture);

        this.barMesh = new Three.InstancedMesh(this.barGeometry, this.barMaterialObj, capacity);
        this.barMesh.frustumCulled = false;
        this.barMesh.userData = { type: 'bar' };
        this.hideAllInstances(this.barMesh, capacity);
        this.barGroup.add(this.barMesh);

        // 바닥판 (프레임당 1개, 원본 defaultBoxMaterial 색)
        const plateHeight = 0.01;
        this.plateGeometry = new Three.BoxBufferGeometry(this.gridSize, plateHeight, this.gridSize * this.hzCount);
        this.plateGeometry.translate(0, plateHeight / 2, this.hzCount / 2 * this.gridSize);
        this.plateMaterial = this.barMaterialKind === 'physical'
            ? new Three.MeshPhysicalMaterial({ side, flatShading: true, color: new Three.Color(0x0c058e) })
            : new Three.MeshLambertMaterial({ side, color: new Three.Color(0x0c058e) });
        this.plateMesh = new Three.InstancedMesh(this.plateGeometry, this.plateMaterial, this.slotCount);
        this.plateMesh.frustumCulled = false;
        this.hideAllInstances(this.plateMesh, this.slotCount);
        this.barGroup.add(this.plateMesh);
    }

    hideAllInstances(mesh, count) {
        this._m4.makeScale(0, 0, 0);
        for (let i = 0; i < count; i++) {
            mesh.setMatrixAt(i, this._m4);
        }
        mesh.instanceMatrix.needsUpdate = true;
    }

    /**
     * graphColors(chroma scale) 를 256 샘플 1D 텍스처로. t = y / 13 (원본 규칙)
     */
    createGradientTexture(colorScale) {
        const size = 256;
        const data = new Uint8Array(size * 4);
        for (let i = 0; i < size; i++) {
            const rgb = colorScale(i / (size - 1)).rgb();
            data[i * 4] = Math.round(rgb[0]);
            data[i * 4 + 1] = Math.round(rgb[1]);
            data[i * 4 + 2] = Math.round(rgb[2]);
            data[i * 4 + 3] = 255;
        }
        const texture = new Three.DataTexture(data, size, 1, Three.RGBAFormat);
        texture.minFilter = Three.LinearFilter;
        texture.magFilter = Three.LinearFilter;
        texture.wrapS = Three.ClampToEdgeWrapping;
        texture.wrapT = Three.ClampToEdgeWrapping;
        texture.needsUpdate = true;
        return texture;
    }

    /**
     * 높이 기반 그라디언트를 주입한 머티리얼
     */
    createBarMaterial(kind, side, gradientTexture) {
        const base = { side, flatShading: true, color: new Three.Color(0xffffff) };
        const material = kind === 'physical'
            ? new Three.MeshPhysicalMaterial(base)
            : new Three.MeshLambertMaterial(base);

        material.onBeforeCompile = (shader) => {
            shader.uniforms.uGradient = { value: gradientTexture };
            shader.uniforms.uGradientScale = { value: 1 / 13 };
            shader.vertexShader = shader.vertexShader
                .replace('#include <common>', '#include <common>\nvarying float vBarY;')
                .replace('#include <begin_vertex>', '#include <begin_vertex>\nvBarY = (instanceMatrix * vec4(position, 1.0)).y;');
            shader.fragmentShader = shader.fragmentShader
                .replace('#include <common>', '#include <common>\nvarying float vBarY;\nuniform sampler2D uGradient;\nuniform float uGradientScale;')
                .replace(
                    'vec4 diffuseColor = vec4( diffuse, opacity );',
                    'vec4 diffuseColor = vec4( diffuse * texture2D(uGradient, vec2(clamp(vBarY * uGradientScale, 0.0, 1.0), 0.5)).rgb, opacity );'
                );
        };
        // onBeforeCompile 내용이 다른 머티리얼과 캐시 충돌하지 않도록
        material.customProgramCacheKey = () => 'fft-gradient-' + kind + '-' + side;
        return material;
    }

    /**
     * 데이터 추가 (FFTChartMerge.addData 와 동일 시그니처/좌표계)
     * @param order 프레임 순번(측정 초)
     * @param ch1 좌뇌 FFT
     * @param ch2 우뇌 FFT
     * @param withRender
     */
    addData(order, ch1, ch2, withRender = true) {
        if (!ch1 || 0 === ch1.length) return;
        if (!this.barMesh) return;

        const t0 = performance.now();

        // 시간 가이드(라인/텍스트) 오래된 프레임 제거 — 원본과 동일 규칙
        if (order > this.maxTimeline) {
            const removed = this.graphGroup.children.slice(0, 1);
            this.graphGroup.children = this.graphGroup.children.slice(1, this.maxTimeline);
            for (const child of removed) {
                this.disposeObject(child); // 시간 라벨 스프라이트 텍스처/라인 geometry 해제
            }
        }

        const data = _.concat(ch1.slice(0, 30).reverse(), new Array(this.hzSpacing).fill(0), ch2 ? ch2.slice(0, 30) : []);
        this.orderIndex = order;

        const slot = ((order % this.slotCount) + this.slotCount) % this.slotCount;
        const baseIndex = slot * this.barsPerFrame;
        const x = (-order * this.gridSize) - (this.gridSize / 2);

        for (let hz = 0; hz < this.barsPerFrame; hz++) {
            let mv = data[hz] || 0;
            mv = mv - (mv * 0.3);
            mv = parseFloat((mv / 2).toFixed(2));
            if (mv > 50) { mv = 50; }

            if (0.1 < mv) {
                this._pos.set(x, 0, hz * this.gridSize + (this.gridSize / 2));
                this._scale.set(1, mv * this.mvSize, 1);
                this._m4.compose(this._pos, this._quat, this._scale);
            } else {
                // 원본은 0.1 이하 막대를 그리지 않음 → 인스턴스 숨김
                this._m4.makeScale(0, 0, 0);
            }
            this.barMesh.setMatrixAt(baseIndex + hz, this._m4);
        }
        this.barMesh.instanceMatrix.needsUpdate = true;

        // 바닥판
        this._pos.set(x, 0, 0);
        this._scale.set(1, 1, 1);
        this._m4.compose(this._pos, this._quat, this._scale);
        this.plateMesh.setMatrixAt(slot, this._m4);
        this.plateMesh.instanceMatrix.needsUpdate = true;

        // XY 가이드 라인 (원본 addData 와 동일)
        if (this.mvGuideLineXY) {
            this.mvGuideBoxMesh2.translateX(this.gridSize);
            for (let i = 0; i < this.mvGuidXLineGroup.length; i++) {
                this.mvGuidXLineGroup[i].geometry.vertices[1].x = order * this.gridSize;
                this.mvGuidXLineGroup[i].geometry.verticesNeedUpdate = true;

                this.mvGuidYLineGroup[i].geometry.vertices[0].x = order * this.gridSize;
                this.mvGuidYLineGroup[i].geometry.vertices[1].x = order * this.gridSize;
                this.mvGuidYLineGroup[i].geometry.verticesNeedUpdate = true;
            }
            for (let i = 0; i < this.mvGuidBYLineGroup.length; i++) {
                this.mvGuidBYLineGroup[i].geometry.vertices[0].x = order * this.gridSize;
                this.mvGuidBYLineGroup[i].geometry.vertices[1].x = order * this.gridSize;
                this.mvGuidBYLineGroup[i].geometry.verticesNeedUpdate = true;
            }
        }

        if ('front' === this.currentCameraType) {
            for (let i = 0; i < this.mvGuidTextGroup.length; i++) {
                this.mvGuidTextGroup[i].position.x += this.gridSize;
            }
        }

        // 시간 가이드 (timeGuideInterval 마다 라인 + 텍스트)
        const graphs = new Three.Object3D();
        graphs.userData = { type: 'timeGuide', order };
        if (this.timeGuide === true && 0 === order % this.timeGuideInterval) {
            const material = new Three.LineBasicMaterial({ color: this.timeGuideColor });
            const geometry = new Three.BufferGeometry().setFromPoints([
                new Three.Vector3(0, 0, 0),
                new Three.Vector3(0, 0, 1),
            ]);
            const line = new Three.Line(geometry, material);
            line.position.x = -order * this.gridSize;
            line.position.z = this.hzCount * this.gridSize;
            const xOffset = 'front' === this.currentCameraType ? 0 : 0.5;
            const text = this.textSprite(`${order}`, line.position.x - xOffset, null, line.position.z + 2.3);
            graphs.add(line);
            graphs.add(text);
        }
        this.graphGroup.add(graphs);

        // 최신 프레임이 x ≈ gridSize/2 에 오도록 그룹 이동 (원본: 호출마다 translateX(gridSize))
        this.graphGroup.translateX(this.gridSize);
        this.barGroup.position.x = this.graphGroup.position.x;

        this.lastAddDataMs = performance.now() - t0;

        if (withRender) {
            this.renderChartOnce();
        }
    }

    /**
     * 카메라 이동 시에만 렌더하는 RAF 루프. 여러 번 호출돼도 루프는 하나만 돈다.
     */
    renderChart() {
        if (this.rafStarted) return;
        this.rafStarted = true;
        const loop = () => {
            if (!this.renderer) return; // disposed
            this.rafHandle = requestAnimationFrame(loop);
            if (this.cameraControls.update(this.clock.getDelta())) {
                this.renderChartOnce();
            }
        };
        loop();
    }

    renderChartOnce() {
        if (!this.renderer) return;
        const t0 = performance.now();
        this.renderer.setRenderTarget(null);
        this.renderer.render(this.scene, this.camera);
        this.lastRenderMs = performance.now() - t0;
    }

    /**
     * 화면 초기화 (FFTChartMerge.clearCanvas 와 동일 동작 + 인스턴스 숨김)
     */
    clearCanvas() {
        const xPos = (this.orderIndex * this.gridSize) * -1;
        this.graphGroup.translateX(xPos);
        this.barGroup.position.x = this.graphGroup.position.x;

        if (this.mvGuideLineXY) {
            this.mvGuideBoxMesh2.translateX(xPos);
            for (let i = 0; i < this.mvGuidXLineGroup.length; i++) {
                this.mvGuidXLineGroup[i].geometry.vertices[1].x = 0;
                this.mvGuidXLineGroup[i].geometry.verticesNeedUpdate = true;

                this.mvGuidYLineGroup[i].geometry.vertices[0].x = 0;
                this.mvGuidYLineGroup[i].geometry.vertices[1].x = 0;
                this.mvGuidYLineGroup[i].geometry.verticesNeedUpdate = true;
            }
            for (let i = 0; i < this.mvGuidBYLineGroup.length; i++) {
                this.mvGuidBYLineGroup[i].geometry.vertices[0].x = 0;
                this.mvGuidBYLineGroup[i].geometry.vertices[1].x = 0;
                this.mvGuidBYLineGroup[i].geometry.verticesNeedUpdate = true;
            }
        }

        if ('front' === this.currentCameraType) {
            for (let i = 0; i < this.mvGuidTextGroup.length; i++) {
                this.mvGuidTextGroup[i].position.x = -3.4;
            }
        }

        // 시간 가이드 정리
        for (const child of this.graphGroup.children) {
            this.disposeObject(child);
        }
        this.graphGroup.children = [];

        if (this.barMesh) {
            this.hideAllInstances(this.barMesh, this.slotCount * this.barsPerFrame);
            this.hideAllInstances(this.plateMesh, this.slotCount);
        }
        this.orderIndex = 0;

        this.renderer.clear();
        this.renderChartOnce();
    }

    /**
     * 원본은 프레임별 Mesh 를 순회해 scale/색을 바꿨다. 인스턴스 구조에서는 해당 없음(호출처 없음).
     */
    setUpscaleRate() {
        this.renderChartOnce();
    }

    /**
     * 진단용 통계
     */
    getStats() {
        const info = this.renderer ? this.renderer.info : null;
        return {
            lastAddDataMs: this.lastAddDataMs,
            lastRenderMs: this.lastRenderMs,
            triangles: info ? info.render.triangles : 0,
            calls: info ? info.render.calls : 0,
            geometries: info ? info.memory.geometries : 0,
            textures: info ? info.memory.textures : 0,
            orderIndex: this.orderIndex,
            engine: 'instanced',
            material: this.barMaterialKind,
            antialias: this.antialias,
        };
    }

    disposeObject(object) {
        object.traverse((o) => {
            if (o.geometry) o.geometry.dispose();
            if (o.material) {
                if (o.material.map) o.material.map.dispose();
                o.material.dispose();
            }
        });
    }

    dispose() {
        if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
        this.rafStarted = false;
        if (this.scene) {
            this.scene.traverse((o) => {
                if (o.geometry) o.geometry.dispose();
                if (o.material) {
                    if (o.material.map) o.material.map.dispose();
                    o.material.dispose();
                }
            });
        }
        if (this.gradientTexture) this.gradientTexture.dispose();
        if (this.cameraControls) this.cameraControls.dispose();
        if (this.renderer) {
            this.renderer.dispose();
            this.renderer = null;
        }
        this.graphGroup = [];
    }
}
